import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ServerInferRequest } from "@ts-rest/core";
import { integrationConnectionContract } from "@common/contracts/integrationConnection.contract";
import { IntegrationConnectionDto } from "@common/dto/integrationConnection/integration-connection.dto";
import { IntegrationConnection } from "@common/types/integrationConnection";
import { useScope } from "@state/scopeContext.lib";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { SUPPRESS_ERROR_TOAST_HEADER } from "@lib/backendClient";
import { clearConnectionError } from "@helpers/connectionError";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { clearIntegrationConnectionErrorInQueryData } from "@queries/entitiesAccounts";
import { invalidateAccountRelatedQueries } from "./account.api";

export const integrationConnectionContractClient = getBackendAPIClient(
    integrationConnectionContract,
);

type GetConnectionsQuery = ServerInferRequest<
    typeof integrationConnectionContract.getConnections
>["query"];

export const integrationConnectionQueryKeys = {
    all: ["integration-connection"] as const,
    queries: (workspaceId: string) =>
        [...integrationConnectionQueryKeys.all, workspaceId] as const,
    query: (workspaceId: string, query?: GetConnectionsQuery) =>
        [
            ...integrationConnectionQueryKeys.queries(workspaceId),
            query ?? {},
        ] as const,
    connection: (workspaceId: string, connectionId: string) =>
        [
            ...integrationConnectionQueryKeys.queries(workspaceId),
            "connection",
            connectionId,
        ] as const,
};

export function useIntegrationConnections(query?: GetConnectionsQuery) {
    const { workspaceId } = useScope();
    const isDemoWorkspace = useActiveWorkspace()?.isDemo ?? false;

    return useQuery({
        queryKey: integrationConnectionQueryKeys.query(
            workspaceId ?? "",
            query,
        ),
        queryFn: async () => {
            if (!workspaceId) {
                return [];
            }
            const response =
                await integrationConnectionContractClient.getConnections({
                    params: {
                        workspaceId,
                    },
                    query,
                });

            return response.body.connections.map(
                IntegrationConnectionDto.deserialize,
            );
        },
        select: (connections) =>
            isDemoWorkspace
                ? connections.map(clearConnectionError)
                : connections,
    });
}

export function useIntegrationConnectionRemovalMutation(connectionId: string) {
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (body: { removeAccountingMigration?: boolean }) => {
            if (!workspaceId) {
                throw new Error("No workspace selected");
            }
            return await integrationConnectionContractClient.removeConnection({
                params: {
                    workspaceId,
                    connectionId,
                },
                body,
                extraHeaders: body.removeAccountingMigration
                    ? { [SUPPRESS_ERROR_TOAST_HEADER]: "true" }
                    : undefined,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: integrationConnectionQueryKeys.all,
            });
            invalidateAccountRelatedQueries({ workspaceId });
        },
    });
}

export function useIntegrationConnectionFixingMutation(
    connection: IntegrationConnection,
    onFixed?: () => void,
) {
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof integrationConnectionContract.fixConnection
            >["body"],
        ) => {
            if (!workspaceId) {
                return;
            }
            await integrationConnectionContractClient.fixConnection({
                params: {
                    workspaceId,
                    connectionId: connection.id,
                },
                body,
            });
        },
        onSuccess: () => {
            clearIntegrationConnectionErrorInQueryData(
                queryClient,
                connection.id,
            );

            queryClient.setQueryData<IntegrationConnection[]>(
                integrationConnectionQueryKeys.query(workspaceId ?? ""),
                (data) =>
                    data?.map((checkedConnection) =>
                        checkedConnection.id === connection.id
                            ? {
                                  ...checkedConnection,
                                  error: null,
                              }
                            : checkedConnection,
                    ) ?? [],
            );
            onFixed?.();
        },
    });
}
